import React, { ReactNode, useState } from "react";
import { Assignment } from "../api/assignments/Assignments.domain";
import { AssignmentsModel } from "../api/assignments/AssignmentsModel";
import { AssignmentsAdapter } from "../api/assignments/AssignmentsAdapter";
import { AppConfigContext } from "./AppConfigContext";
import { useAlert } from "../hooks/useAlert";
import { useAuth } from "./controllers/useAuth";
import { useNavigate } from "react-router-dom";
import { AssignmentAcceptanceStatus, MissionStatus } from "../api/assignments/Assignments.codes";
import {
  AssignmentDetailOutput,
  AssignmentListResponseOutput,
  UpdateAcceptanceRequest,
  UpdateAcceptanceResponse,
  UpdateAssignmentRequestInput,
  PaginationInfo,
  ProviderServicePointsSummaryOutput,
  ProviderServicePointsRequestInput,
  UpdateAdjustoIdRequest,
  UpdateAdjustoIdResponse,
  UpdateMissionStatusRequest,
  UpdateMissionStatusResponse,
} from "../api/assignments/Assignments.responses";

interface AssignmentsProviderProps {
  children: ReactNode;
}

interface AssignmentsContextType {
  updateAssignment: (
    externalAssignmentId: string,
    updateData: UpdateAssignmentRequestInput,
  ) => Promise<AssignmentDetailOutput | undefined>;
  getAssignmentsByAcceptanceStatus: (
    status: AssignmentAcceptanceStatus,
    missionStatus: MissionStatus,
    page?: number,
    size?: number,
  ) => Promise<AssignmentListResponseOutput | undefined>;
  updateAcceptance: (
    externalAssignmentId: string,
    updateData: UpdateAcceptanceRequest,
  ) => Promise<UpdateAcceptanceResponse | undefined>;
  searchAssignment: (searchParams: {
    adjustoId?: string;
    externalId?: string;
    claimId?: string;
  }) => Promise<AssignmentDetailOutput | undefined>;
  getAssignment: (externalAssignmentId: string) => Promise<AssignmentDetailOutput | undefined>;
  assignments?: Assignment[];
  pagination?: PaginationInfo;
  getServiceProviders: () => Promise<ProviderServicePointsSummaryOutput | undefined>;
  updateProviderInformations: (
    providerId: string,
    updateData: ProviderServicePointsRequestInput,
  ) => Promise<ProviderServicePointsSummaryOutput | undefined>;
  updateAdjustoId: (
    externalAssignmentId: string,
    updateData: UpdateAdjustoIdRequest,
  ) => Promise<UpdateAdjustoIdResponse | undefined>;
  updateMissionStatus: (
    externalAssignmentId: string,
    updateData: UpdateMissionStatusRequest,
  ) => Promise<UpdateMissionStatusResponse | undefined>;
  exportAssignmentsByMonth: (year: number, month: number) => Promise<Blob | undefined>;
}

export const AssignmentsContext = React.createContext<AssignmentsContextType>(null!);

export function AssignmentsProvider({ children }: AssignmentsProviderProps) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const { getToken, logoutUnauthorized } = useAuth();
  const navigate = useNavigate();
  const assignmentsModel = new AssignmentsModel(appConfig!);
  const assignmentsAdapter = new AssignmentsAdapter(assignmentsModel);

  const [assignments, setAssignments] = useState<Assignment[]>();
  const [pagination, setPagination] = useState<PaginationInfo>();

  const handleUnauthorized = () => {
    logoutUnauthorized();
    navigate("/login");
  };

  const updateAssignment = async (
    externalAssignmentId: string,
    updateData: UpdateAssignmentRequestInput,
  ): Promise<AssignmentDetailOutput | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsModel.updateAssignment(
        externalAssignmentId,
        updateData,
        token,
      );

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(`Échec de la mise à jour de l'assignment : HTTP ${response.status}`);
      }

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const getAssignmentsByAcceptanceStatus = async (
    status: AssignmentAcceptanceStatus,
    missionStatus: MissionStatus,
    page: number = 1,
    size: number = 10,
  ): Promise<AssignmentListResponseOutput | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsModel.getAssignmentsByAcceptanceStatus(
        status,
        missionStatus,
        token,
        page,
        size,
      );

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(
          `Échec de la récupération des assignments par statut : HTTP ${response.status}`,
        );
      }

      setPagination(response.data.pagination);
      setAssignments(response.data.response);

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const updateAcceptance = async (
    externalAssignmentId: string,
    updateData: UpdateAcceptanceRequest,
  ): Promise<UpdateAcceptanceResponse | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsModel.updateAcceptance(
        externalAssignmentId,
        updateData,
        token,
      );

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(`Échec de la mise à jour de l'acceptation : HTTP ${response.status}`);
      }

      if (updateData.acceptanceStatus === AssignmentAcceptanceStatus.ACCEPTED) {
        showAlert("Mission acceptée avec succès.", "success");
      } else if (updateData.acceptanceStatus === AssignmentAcceptanceStatus.REJECTED) {
        showAlert("Mission refusée avec succès.", "success");
      }

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const searchAssignment = async (searchParams: {
    adjustoId?: string;
    externalId?: string;
    claimId?: string;
  }): Promise<AssignmentDetailOutput | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsModel.searchAssignment(searchParams, token);

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status === 204) {
        showAlert("Aucun assignment trouvé", "warning");
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(`Échec de la recherche d'assignment : HTTP ${response.status}`);
      }

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const getServiceProviders = async (): Promise<ProviderServicePointsSummaryOutput | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsAdapter.getServiceProviders(token);

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(`Échec de la récupération des providers : HTTP ${response.status}`);
      }

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const updateProviderInformations = async (
    providerId: string,
    updateData: ProviderServicePointsRequestInput,
  ): Promise<ProviderServicePointsSummaryOutput | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsAdapter.updateProviderInformations(
        providerId,
        updateData,
        token,
      );

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(
          `Échec de la mise à jour des informations du provider : HTTP ${response.status}`,
        );
      }

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const getAssignment = async (
    externalAssignmentId: string,
  ): Promise<AssignmentDetailOutput | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsAdapter.getAssignment(externalAssignmentId, token);

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status === 204) {
        showAlert("Aucun assignment trouvé", "warning");
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(`Échec de la récupération de l'assignment : HTTP ${response.status}`);
      }

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const updateAdjustoId = async (
    externalAssignmentId: string,
    updateData: UpdateAdjustoIdRequest,
  ): Promise<UpdateAdjustoIdResponse | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsAdapter.updateAdjustoId(
        externalAssignmentId,
        updateData,
        token,
      );

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(`Échec de la mise à jour de l'adjustoId : HTTP ${response.status}`);
      }

      showAlert("Identifiant Adjusto mis à jour avec succès.", "success");
      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const updateMissionStatus = async (
    externalAssignmentId: string,
    updateData: UpdateMissionStatusRequest,
  ): Promise<UpdateMissionStatusResponse | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsAdapter.updateMissionStatus(
        externalAssignmentId,
        updateData,
        token,
      );

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(
          `Échec de la mise à jour du statut de prestation : HTTP ${response.status}`,
        );
      }

      showAlert("Statut de prestation mis à jour avec succès.", "success");
      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const exportAssignmentsByMonth = async (
    year: number,
    month: number,
  ): Promise<Blob | undefined> => {
    try {
      const token = getToken();
      if (!token) throw new Error("Le jeton d'authentification est manquant.");

      const response = await assignmentsAdapter.exportAssignmentsByMonth(year, month, token);

      if (response.status === 401) {
        handleUnauthorized();
        return undefined;
      }

      if (response.status === 204) {
        showAlert("Aucune prestation trouvée pour cette période", "warning");
        return undefined;
      }

      if (response.status !== 200 || !response.data) {
        throw new Error(`Échec de l'export des prestations : HTTP ${response.status}`);
      }

      return response.data;
    } catch (err) {
      if (err instanceof Error) {
        showAlert(err.message, "danger");
      }
    }
  };

  const contextValue = {
    updateAssignment,
    getAssignmentsByAcceptanceStatus,
    assignments,
    pagination,
    updateAcceptance,
    searchAssignment,
    getServiceProviders,
    updateProviderInformations,
    getAssignment,
    updateAdjustoId,
    updateMissionStatus,
    exportAssignmentsByMonth,
  };

  return <AssignmentsContext.Provider value={contextValue}>{children}</AssignmentsContext.Provider>;
}
